/**
 * Server-rendered grass-vs-grass comparison table for /compare/[slug] pages.
 * Pure presentational. Liquid-glass styling lives in compare-glass.css
 * (`.cmp-table` desktop / `.cmp-tbl-m` mobile). A = green, B = amber.
 *
 * `winner`/`winnerBadgeLabel` are set client-side after a ZIP verdict resolves
 * (null on the server render, so SSG HTML is identical for every visitor).
 */

import type { ComparisonRow } from '@/data/comparisons'

interface GrassVsGrassTableProps {
  aLabel: string
  bLabel: string
  rows: ComparisonRow[]
  winner?: 'a' | 'b' | null
  winnerBadgeLabel?: string
}

function WinnerBadge({ label }: { label: string }) {
  return <span className="cmp-table__winbadge">{label}</span>
}

export function GrassVsGrassTable({
  aLabel,
  bLabel,
  rows,
  winner = null,
  winnerBadgeLabel,
}: GrassVsGrassTableProps) {
  const badge = winner && winnerBadgeLabel ? winnerBadgeLabel : null
  return (
    <>
      {/* Desktop: 3-column glass table with tinted A/B columns */}
      <div className="cmp-tablewrap hidden md:block">
        <table className="cmp-table" data-winner={winner ?? undefined}>
          <thead>
            <tr>
              <th>Trait</th>
              <th className="a">
                <span className="sw" style={{ background: '#16a34a' }} />
                {aLabel}
                {badge && winner === 'a' && <WinnerBadge label={badge} />}
              </th>
              <th className="b">
                <span className="sw" style={{ background: '#d97706' }} />
                {bLabel}
                {badge && winner === 'b' && <WinnerBadge label={badge} />}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td className="a">{row.a}</td>
                <td className="b">{row.b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked per-feature, A then B. One caption above the list
          rather than per-row badges (recognition without noise). */}
      <div className="cmp-tbl-m md:hidden" data-winner={winner ?? undefined}>
        {badge && (
          <p className="cmp-tbl-m__wincap">
            <span className="cmp-table__winbadge">{badge}</span>{' '}
            {winner === 'a' ? aLabel : bLabel}
          </p>
        )}
        {rows.map((row) => (
          <div key={row.label} className="cmp-tbl-m__row">
            <p className="cmp-tbl-m__label">{row.label}</p>
            <div className="cmp-tbl-m__pair">
              <div className="cmp-tbl-m__a">
                <div className="cmp-tbl-m__name">{aLabel}</div>
                <div className="cmp-tbl-m__val">{row.a}</div>
              </div>
              <div className="cmp-tbl-m__b">
                <div className="cmp-tbl-m__name">{bLabel}</div>
                <div className="cmp-tbl-m__val">{row.b}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
